import { IChargeMiles, IChargeMoney } from '@smiles/core-traveler-universe/dist/Types/Hotels/Details.types';
import { IHotels } from '@smiles/core-traveler-universe/dist/Types/Hotels/Results.types';
import moment from 'moment';
import { IFilterSearch } from '../store/duck/filterSearch';
import { ISearchReduxState } from '../store/duck/search';

export function getFiltersWithCount(filterValues: IFilterSearch) {
  const {
    rules = {},
    accommodations = {},
    instalations = {},
    amenities = {},
    categoryStars = {},
    centerDistance = {},
    bedPreference = {},
    rangePrice,
    rangeMiles,
  } = filterValues;

  let filtersCount = 0;
  filtersCount += Object.entries(filterValues).filter((i) => i.includes(true)).length;
  filtersCount += Object.entries(rules).filter((i) => i.includes(true)).length;
  filtersCount += Object.entries(accommodations).filter((i) => i.includes(true)).length;
  filtersCount += Object.entries(instalations).filter((i) => i.includes(true)).length;
  filtersCount += Object.entries(amenities).filter((i) => i.includes(true)).length;
  filtersCount += Object.entries(categoryStars).filter((i) => i.includes(true)).length;
  filtersCount += Object.entries(centerDistance).filter((i) => i.includes(true)).length;
  filtersCount += Object.entries(bedPreference).filter((i) => i.includes(true)).length;
  if (rangePrice && (rangePrice.min > 50 || rangePrice.max < 6000)) {
    filtersCount += 1;
  }
  if (rangeMiles && (rangeMiles.min > 2000 || rangeMiles.max < 200000)) {
    filtersCount += 1;
  }

  return { ...filterValues, filtersCount };
}

export const parseSearchPanelDuckToQueryParams = (searchPanelDuck: ISearchReduxState) => {
  const params = Object.fromEntries([...new URLSearchParams(window.location.search).entries()]);
  const { checkin, checkout, rooms = [{ id: 1, adults: 1, children: [] }], name, region, id } = searchPanelDuck;

  const roomsCount = [];

  for (let i = 0; i < rooms.length; i += 1) {
    const adults = rooms[i].adults;
    const children = rooms[i].children.map((c: any) => c.age).join(',');

    let roomsFormatted = '';
    roomsFormatted = children ? `${adults}-${children}` : `${adults}`;

    roomsCount.push(roomsFormatted);
  }

  const DATE_FORMAT = 'YYYY-MM-DD';

  const objectstringify = {
    checkin: checkin
      ? moment(checkin).format(DATE_FORMAT).toString()
      : moment(params.checkin).format(DATE_FORMAT).toString(),
    checkout: checkout
      ? moment(checkout).format(DATE_FORMAT).toString()
      : moment(params.checkout).format(DATE_FORMAT).toString(),
    name,
    region: `${name} ${region}`,
    rooms: roomsCount.join(':'),
    destination: id,
  } as any;

  Object.entries(objectstringify).forEach((entry) => {
    const [key, value] = entry;
    objectstringify[key] = value;
  });

  let queryString = '';

  Object.entries(objectstringify).forEach((entry) => {
    if (entry[1]) {
      queryString += `${entry[0]}=${entry[1]}&`;
    }
  });

  queryString = queryString.substring(0, queryString.length - 1);

  return `${queryString}&`;
};

export const setQueryParamsToSearchPanel = (params: [string, string][]): any => {
  const objectParams = {} as any;

  params.forEach((p) => {
    const [key, value] = p;
    objectParams[key] = value;
  });

  const rooms = [];
  const roomsList = objectParams?.rooms?.split(':') || [];

  for (let i = 0; i < roomsList.length; i += 1) {
    const room = roomsList[i];
    const adults = room.split('-')[0];
    const children =
      room
        .split('-')[1]
        ?.split(',')
        ?.map((value: string, index: number) => ({
          id: index,
          age: Number(value),
        })) || [];

    rooms.push({
      id: i + 1,
      adults: Number(adults),
      children,
    });
  }

  return {
    ...objectParams,
    rooms,
    id: Number(objectParams.id || objectParams.destination || 0),
  };
};

export const getFiltersQueryString = (filterValues: IFilterSearch) => {
  const objectParams = {
    orderby: filterValues.orderby,
    breakfast: Boolean(filterValues.breakfast),
    freeCancellation: Boolean(filterValues.freeCancellation),
    allowAnimals: Boolean(filterValues.allowAnimals),
    'rangePrice.min': filterValues.rangePrice?.min,
    'rangePrice.max': filterValues.rangePrice?.max,
    'rangeMiles.min': filterValues.rangeMiles?.min,
    'rangeMiles.max': filterValues.rangeMiles?.max,
    'categoryStars.one': Boolean(filterValues?.categoryStars?.one),
    'categoryStars.two': Boolean(filterValues?.categoryStars?.two),
    'categoryStars.three': Boolean(filterValues?.categoryStars?.three),
    'categoryStars.four': Boolean(filterValues?.categoryStars?.four),
    'categoryStars.five': Boolean(filterValues?.categoryStars?.five),
  };

  const objectParamsAvailable = {} as any;

  Object.entries(objectParams).forEach((entry) => {
    const [key, value] = entry;
    if (value) {
      objectParamsAvailable[key] = value;
    }
  });

  return new URLSearchParams(objectParamsAvailable).toString();
};

export const setQueryParamsToFilterSearch = (query: [string, string][]) => {
  const obj = {} as any;
  const allowFilters = {
    orderby: (value: string) => {
      obj.orderby = value;
    },
    breakfast: (value: string) => {
      obj.breakfast = value === 'true';
    },
    allowAnimals: (value: string) => {
      obj.allowAnimals = value === 'true';
    },
    freeCancellation: (value: string) => {
      obj.freeCancellation = value === 'true';
    },
    'rangePrice.min': (value: number) => {
      obj.rangePrice = {};
      obj.rangePrice.min = Number(value);
    },
    'rangePrice.max': (value: number) => {
      obj.rangePrice = { ...obj.rangePrice };
      obj.rangePrice.max = Number(value);
    },
    'rangeMiles.min': (value: number) => {
      obj.rangeMiles = {};
      obj.rangeMiles.min = Number(value);
    },
    'rangeMiles.max': (value: number) => {
      obj.rangeMiles = { ...obj.rangeMiles };
      obj.rangeMiles.max = Number(value);
    },
    'rules.party': (value: string) => {
      obj.rules = {};
      obj.rules.party = value === 'true';
    },
    'rules.pets': (value: string) => {
      obj.rules = { ...obj.rules };
      obj.rules.pets = value === 'true';
    },
    'rules.smoke': (value: string) => {
      obj.rules = { ...obj.rules };
      obj.rules.smoke = value === 'true';
    },
    'accommodations.apartment': (value: string) => {
      obj.accommodations = {};
      obj.accommodations.apartment = value === 'true';
    },
    'accommodations.cottage': (value: string) => {
      obj.accommodations = { ...obj.accommodations };
      obj.accommodations.cottage = value === 'true';
    },
    'accommodations.hotels': (value: string) => {
      obj.accommodations = { ...obj.accommodations };
      obj.accommodations.hotels = value === 'true';
    },
    'accommodations.inns': (value: string) => {
      obj.accommodations = { ...obj.accommodations };
      obj.accommodations.inns = value === 'true';
    },
    'accommodations.privateHomes': (value: string) => {
      obj.accommodations = { ...obj.accommodations };
      obj.accommodations.privateHomes = value === 'true';
    },
    'accommodations.sharedRoom': (value: string) => {
      obj.accommodations = { ...obj.accommodations };
      obj.accommodations.sharedRoom = value === 'true';
    },
    'instalations.gym': (value: string) => {
      obj.instalations = {};
      obj.instalations.gym = value === 'true';
    },
    'instalations.park': (value: string) => {
      obj.instalations = { ...obj.instalations };
      obj.instalations.park = value === 'true';
    },
    'instalations.pool': (value: string) => {
      obj.instalations = { ...obj.instalations };
      obj.instalations.pool = value === 'true';
    },
    'instalations.wheelchairAccess': (value: string) => {
      obj.instalations = { ...obj.instalations };
      obj.instalations.wheelchairAccess = value === 'true';
    },
    'amenities.airConditioner': (value: string) => {
      obj.amenities = {};
      obj.amenities.airConditioner = value === 'true';
    },
    'amenities.freeWifi': (value: string) => {
      obj.amenities = { ...obj.amenities };
      obj.amenities.freeWifi = value === 'true';
    },
    'amenities.reception24h': (value: string) => {
      obj.amenities = { ...obj.amenities };
      obj.amenities.reception24h = value === 'true';
    },
    'amenities.roomService': (value: string) => {
      obj.amenities = { ...obj.amenities };
      obj.amenities.roomService = value === 'true';
    },
    'categoryStars.one': (value: string) => {
      obj.categoryStars = {};
      obj.categoryStars.one = value === 'true';
    },
    'categoryStars.two': (value: string) => {
      obj.categoryStars = { ...obj.categoryStars };
      obj.categoryStars.two = value === 'true';
    },
    'categoryStars.three': (value: string) => {
      obj.categoryStars = { ...obj.categoryStars };
      obj.categoryStars.three = value === 'true';
    },
    'categoryStars.four': (value: string) => {
      obj.categoryStars = { ...obj.categoryStars };
      obj.categoryStars.four = value === 'true';
    },
    'categoryStars.five': (value: string) => {
      obj.categoryStars = { ...obj.categoryStars };
      obj.categoryStars.five = value === 'true';
    },
    'centerDistance.oneKilometer': (value: string) => {
      obj.centerDistance = {};
      obj.centerDistance.oneKilometer = value === 'true';
    },
    'centerDistance.threeKilometers': (value: string) => {
      obj.centerDistance = { ...obj.centerDistance };
      obj.centerDistance.threeKilometers = value === 'true';
    },
    'centerDistance.fiveKilometers': (value: string) => {
      obj.centerDistance = { ...obj.centerDistance };
      obj.centerDistance.fiveKilometers = value === 'true';
    },
    'bedPreference.marriedBed': (value: string) => {
      obj.bedPreference = {};
      obj.bedPreference.marriedBed = value === 'true';
    },
    'bedPreference.singleBed': (value: string) => {
      obj.bedPreference = { ...obj.bedPreference };
      obj.bedPreference.singleBed = value === 'true';
    },
  } as any;

  query.forEach((currentKey) => {
    const key = Object.entries(allowFilters).find((allowKey) => allowKey[0] === currentKey[0]) as [string, Function];

    if (key?.length) {
      key[1](currentKey[1]);
    }
  });

  return obj;
};

export const buildSearchUrl = (searchInfo: ISearchReduxState, filterSearchDuck: IFilterSearch, baseRoute: string) => {
  const initalParams = parseSearchPanelDuckToQueryParams(searchInfo);

  return `${baseRoute}?${initalParams}${getFiltersQueryString(filterSearchDuck)}`;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export function filterHotelList(filterComputed: getFiltersWithCountType, hoteisList: IHotels['hotelAvail']) {
  let filteredHotels = hoteisList;

  if (filterComputed.breakfast) {
    filteredHotels = hoteisList.filter((h) => {
      return h.lowestFare.boardTypes.boardType.find((i) => i.name.toLowerCase().includes('café'));
    });
  }

  // categoryStars
  const starsAllowed = {
    one() {
      return 1;
    },
    two() {
      return 2;
    },
    three() {
      return 3;
    },
    four() {
      return 4;
    },
    five() {
      return 5;
    },
  };

  type allowedStars = keyof typeof starsAllowed;

  if (filterComputed.categoryStars) {
    const starsSelected = Object.entries(filterComputed.categoryStars).find((i) => i[1]);
    if (starsSelected) {
      const currentStars = Object.entries(filterComputed.categoryStars)
        .filter((i) => i[1])
        .map((i) => {
          return starsAllowed[i[0] as allowedStars]();
        });

      filteredHotels = filteredHotels.filter((h) => {
        return currentStars.includes(Math.floor(h.hotel.stars));
      });
    }
  }

  /**
   * Cancelamento Gratuito
   */

  if (filterComputed.freeCancellation) {
    filteredHotels = filteredHotels.filter((h) => {
      return h.lowestFare.cancellationPolicies.refundable;
    });
  }

  if (filterComputed.rangePrice && (filterComputed.rangePrice.min > 50 || filterComputed.rangePrice.max < 6000)) {
    filteredHotels = filteredHotels.filter((h) => {
      return (
        (h.charges.charge.find((charge) => charge.type === 'MONEY') as IChargeMoney)?.dailyWithServiceCharge >=
          filterComputed.rangePrice.min &&
        (h.charges.charge.find((charge) => charge.type === 'MONEY') as IChargeMoney)?.dailyWithServiceCharge <=
          filterComputed.rangePrice.max
      );
    });
  }

  if (filterComputed.rangeMiles && (filterComputed.rangeMiles.min > 2000 || filterComputed.rangeMiles.max < 200000)) {
    filteredHotels = filteredHotels.filter((h) => {
      return (
        (h.charges.charge.find((charge) => charge.type === 'SMILES') as IChargeMiles)?.dailyMilesClub >=
          filterComputed.rangeMiles.min &&
        (h.charges.charge.find((charge) => charge.type === 'SMILES') as IChargeMiles)?.dailyMilesClub <=
          filterComputed.rangeMiles.max
      );
    });
  }

  if (filterComputed.orderby && filterComputed.orderby === 'rating') {
    filteredHotels = [...filteredHotels].sort((a, b) => {
      if (a.hotel.stars > b.hotel.stars) {
        return -1;
      }
      return 1;
    });
  }

  if (filterComputed.orderby && filterComputed.orderby === 'highestPrice') {
    filteredHotels = [...filteredHotels].sort((a, b) => {
      if (
        (a.charges.charge.find((charge) => charge.type === 'SMILES') as IChargeMiles)?.dailyMiles >
        (b.charges.charge.find((charge) => charge.type === 'SMILES') as IChargeMiles)?.dailyMiles
      ) {
        return -1;
      }
      return 1;
    });
  }

  if (filterComputed.orderby && filterComputed.orderby === 'lowestPrice') {
    filteredHotels = [...filteredHotels].sort((a, b) => {
      if (
        (a.charges.charge.find((charge) => charge.type === 'SMILES') as IChargeMiles)?.dailyMiles <
        (b.charges.charge.find((charge) => charge.type === 'SMILES') as IChargeMiles)?.dailyMiles
      ) {
        return -1;
      }
      return 1;
    });
  }

  return filteredHotels;
}

export type getFiltersWithCountType = ReturnType<typeof getFiltersWithCount>;
